export type LineupLoadMatch = {
  id: string;
  teamId: string;
  matchDate: string;
  opponentName: string | null;
  lineupStatus: string | null;
  matchStatus: string;
};

export type LineupLoadEntry = {
  matchId: string;
  playerId: string;
};

export type LineupLoadAssignment = LineupLoadMatch & {
  playerId: string;
};

export type ThreeDayLineupWarning = {
  playerId: string;
  matchId: string;
  assignments: LineupLoadAssignment[];
};

const savedLineupStatuses = new Set(['Draft', 'Published']);

function dateDistance(first: string, second: string): number {
  const [firstYear, firstMonth, firstDay] = first.split('-').map(Number);
  const [secondYear, secondMonth, secondDay] = second.split('-').map(Number);
  const firstUtc = Date.UTC(firstYear, firstMonth - 1, firstDay);
  const secondUtc = Date.UTC(secondYear, secondMonth - 1, secondDay);
  return Math.abs(firstUtc - secondUtc) / 86_400_000;
}

/**
 * A three-calendar-day span includes dates no more than two days apart: for
 * example, Monday through Wednesday. Standby rows are intentionally absent
 * from the input; only saved lineup entries represent an assignment to play.
 */
export function buildThreeDayLineupWarnings(
  matches: LineupLoadMatch[],
  entries: LineupLoadEntry[],
): Map<string, ThreeDayLineupWarning> {
  const matchById = new Map(matches
    .filter((match) => match.matchStatus !== 'Cancelled' && savedLineupStatuses.has(match.lineupStatus || ''))
    .map((match) => [match.id, match]));
  const assignmentsByPlayer = new Map<string, LineupLoadAssignment[]>();
  const uniqueAssignments = new Set<string>();

  entries.forEach((entry) => {
    const match = matchById.get(entry.matchId);
    const key = `${entry.matchId}:${entry.playerId}`;
    if (!match || uniqueAssignments.has(key)) return;
    uniqueAssignments.add(key);
    assignmentsByPlayer.set(entry.playerId, [...(assignmentsByPlayer.get(entry.playerId) || []), { ...match, playerId: entry.playerId }]);
  });

  const warnings = new Map<string, ThreeDayLineupWarning>();
  assignmentsByPlayer.forEach((assignments, playerId) => {
    const ordered = [...assignments].sort((left, right) => left.matchDate.localeCompare(right.matchDate) || left.id.localeCompare(right.id));
    ordered.forEach((assignment) => {
      const nearby = ordered.filter((candidate) => dateDistance(assignment.matchDate, candidate.matchDate) <= 2);
      if (nearby.length >= 2) warnings.set(`${assignment.id}:${playerId}`, { playerId, matchId: assignment.id, assignments: nearby });
    });
  });
  return warnings;
}
